<template>
    <div class="ds-ex-select" v-click-outside="close" @keydown.esc.stop="close">
        <button
            type="button"
            class="ds-ex-field ds-ex-select-trigger"
            :class="{ 'is-open': isOpen, 'is-placeholder': !selected }"
            :disabled="disabled"
            :aria-expanded="isOpen"
            aria-haspopup="listbox"
            :aria-label="label"
            @click="toggle"
        >
            <span class="ds-ex-ellipsis">{{ selected?.name ?? placeholder }}</span>
            <svg class="ds-ex-chevron" :class="{ 'is-open': isOpen }" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
        </button>
        <div v-if="isOpen" class="ds-glass-menu ds-ex-select-menu" role="listbox" :aria-label="label">
            <label v-if="searchable" class="ds-ex-select-search">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
                <input ref="searchInput" v-model="search" type="text" :placeholder="searchPlaceholder" maxlength="100" @input="onSearch">
            </label>
            <div v-if="heading" class="ds-glass-menu-heading">{{ heading }}</div>
            <div class="ds-ex-select-list">
                <button
                    v-for="option in visibleOptions"
                    :key="option.id"
                    type="button"
                    role="option"
                    class="ds-glass-menu-item ds-ex-select-option"
                    :class="{ 'is-selected': option.id === modelValue }"
                    :aria-selected="option.id === modelValue"
                    @click="choose(option.id)"
                >
                    <ExchangeAvatar v-if="option.person" :person="option.person" size="sm" />
                    <span class="ds-ex-select-option-text">
                        <span class="ds-ex-ellipsis">{{ option.name }}</span>
                        <span v-if="option.subtitle" class="ds-ex-select-option-sub ds-ex-ellipsis">{{ option.subtitle }}</span>
                    </span>
                    <svg v-if="option.id === modelValue" class="ds-ex-select-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>
                </button>
                <div v-if="loading" class="ds-ex-select-empty">Загружаем…</div>
                <div v-else-if="!visibleOptions.length" class="ds-ex-select-empty">{{ emptyText }}</div>
            </div>
            <div v-if="footnote" class="ds-ex-select-footnote">{{ footnote }}</div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import debounce from 'lodash.debounce'
import ExchangeAvatar from './ExchangeAvatar.vue'

export interface SelectOption {
    id: string
    name: string
    subtitle?: string
    person?: { id: string, first_name: string, last_name: string, avatar?: string | null }
}

const props = withDefaults(defineProps<{
    options: SelectOption[]
    label: string
    placeholder?: string
    disabled?: boolean
    searchable?: boolean
    // Серверный поиск: локальная фильтрация не применяется, родитель получает текст.
    remoteSearch?: boolean
    searchPlaceholder?: string
    heading?: string
    footnote?: string
    emptyText?: string
    loading?: boolean
}>(), {
    placeholder: 'Выберите',
    disabled: false,
    searchable: false,
    remoteSearch: false,
    searchPlaceholder: 'Поиск',
    heading: '',
    footnote: '',
    emptyText: 'Ничего не нашли',
    loading: false,
})

const modelValue = defineModel<string>({ default: '' })
const emit = defineEmits<{ search: [text: string], open: [] }>()

const isOpen = ref(false)
const search = ref('')
const searchInput = ref<HTMLInputElement | null>(null)

const selected = computed(() => props.options.find(option => option.id === modelValue.value) ?? null)
const visibleOptions = computed(() => {
    const text = search.value.trim().toLowerCase()
    if (!text || props.remoteSearch) return props.options
    return props.options.filter(option => `${option.name} ${option.subtitle ?? ''}`.toLowerCase().includes(text))
})

const emitSearch = debounce((text: string) => emit('search', text), 400)
const onSearch = () => { if (props.remoteSearch) emitSearch(search.value) }

async function toggle() {
    if (props.disabled) return
    isOpen.value = !isOpen.value
    if (isOpen.value) {
        emit('open')
        await nextTick()
        searchInput.value?.focus()
    }
}

function close() {
    if (!isOpen.value) return
    isOpen.value = false
    search.value = ''
}

function choose(id: string) {
    modelValue.value = id
    close()
}
</script>
